import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { Receipt } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { formatCurrency } from '@/lib/utils/currency';
import { useExpenseMutations } from '../hooks';
import { CURRENCY_VALUES, DEFAULT_CURRENCY } from '../types';
import type { Currency, Expense } from '../types';

interface ExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → create; an expense → edit */
  expense: Expense | null;
}

// Create / edit a company expense. Category, matter (case) and contract are
// optional links resolved through the shared UniversalCombobox.
export function ExpenseDialog({ open, onOpenChange, expense }: ExpenseDialogProps) {
  const { t } = useTranslation();
  const { createMutation, updateMutation } = useExpenseMutations();

  const [rate, setRate] = React.useState('');
  const [quantity, setQuantity] = React.useState('');
  const [currency, setCurrency] = React.useState<Currency>(DEFAULT_CURRENCY);
  const [categoryId, setCategoryId] = React.useState<number | undefined>(undefined);
  const [matterId, setMatterId] = React.useState<number | undefined>(undefined);
  const [contractId, setContractId] = React.useState<number | undefined>(undefined);
  // The selected matter's own contract — used to keep the two fields consistent.
  const [matterContractId, setMatterContractId] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (open) {
      setRate(expense?.rate != null ? String(expense.rate) : '');
      setQuantity(expense?.quantity != null ? String(expense.quantity) : '');
      setCurrency(expense?.currency ?? DEFAULT_CURRENCY);
      setCategoryId(expense?.expenseCategoryId ?? undefined);
      setMatterId(expense?.matterId ?? undefined);
      setContractId(expense?.contractId ?? undefined);
      setMatterContractId(undefined);
    }
  }, [open, expense]);

  // Contract drives the case list. Changing it to a different contract drops a
  // now-out-of-scope case selection.
  const handleContractSelect = (item: Record<string, unknown> | null) => {
    const newId = (item?.id as number | undefined) ?? undefined;
    setContractId(newId);
    if (newId && matterContractId && matterContractId !== newId) {
      setMatterId(undefined);
      setMatterContractId(undefined);
    }
  };

  // Picking a case records its contract; if no contract is chosen yet, the
  // case's contract auto-fills the contract field.
  const handleMatterSelect = (item: Record<string, unknown> | null) => {
    setMatterId((item?.id as number | undefined) ?? undefined);
    const contract = item?.contract as { id?: number } | undefined;
    const mContractId =
      (item?.contractId as number | undefined) ?? contract?.id ?? undefined;
    setMatterContractId(mContractId);
    if (!contractId && mContractId) {
      setContractId(mContractId);
    }
  };

  const rateNum = Number(rate);
  const quantityNum = Number(quantity);
  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    rate.trim() !== '' &&
    quantity.trim() !== '' &&
    Number.isFinite(rateNum) &&
    rateNum >= 0 &&
    Number.isInteger(quantityNum) &&
    quantityNum >= 0 &&
    !isPending;

  const amountPreview = canSubmit ? rateNum * quantityNum : 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const payload = {
      rate: rateNum,
      quantity: quantityNum,
      currency,
      ...(categoryId ? { expenseCategoryId: categoryId } : {}),
      ...(matterId ? { matterId } : {}),
      ...(contractId ? { contractId } : {}),
    };
    try {
      if (expense) {
        await updateMutation.mutateAsync({ id: expense.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch {
      // errors are surfaced via the mutation's onError toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-slate-500" />
            {expense ? t('expense.editDialogTitle') : t('expense.addDialogTitle')}
          </DialogTitle>
          <DialogDescription>{t('expense.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('expense.categoryLabel')}
            </label>
            <UniversalCombobox
              url="/expense-category"
              labelKey="name"
              placeholder={t('expense.categoryPlaceholder')}
              queryKey="expenseCategoryCombo"
              selectedId={categoryId}
              onSelectItem={(item) => setCategoryId(item?.id as number | undefined)}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('expense.rateLabel')}
              </label>
              <NumericFormat
                customInput={Input}
                thousandSeparator=" "
                allowNegative={false}
                decimalScale={0}
                value={rate}
                onValueChange={(v) => setRate(v.value ?? '')}
                placeholder="0"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('expense.quantityLabel')}
              </label>
              <Input
                type="number"
                min={0}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="1"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('expense.currencyLabel')}
              </label>
              <Select value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_VALUES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {t('expense.amountLabel')}:{' '}
            <span className="font-semibold text-slate-900">
              {formatCurrency(amountPreview, { currency })}
            </span>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('expense.contractLabel')}
            </label>
            <UniversalCombobox
              url="/contracts"
              labelKey="name"
              placeholder={t('expense.contractPlaceholder')}
              queryKey="expenseContractCombo"
              selectedId={contractId}
              onSelectItem={handleContractSelect}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('expense.matterLabel')}
            </label>
            <UniversalCombobox
              url="/matters"
              labelKey="name"
              placeholder={t('expense.matterPlaceholder')}
              queryKey="expenseMatterCombo"
              params={contractId ? { contractId } : undefined}
              selectedId={matterId}
              onSelectItem={handleMatterSelect}
            />
            {contractId && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('expense.matterScopedHint')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isPending ? t('processing') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
